<script setup lang="ts">
import { HubTable } from '@/modules/Workspaces/KnowledgeHub/Hubs/FileTable'
import FolderRow from '@/modules/Workspaces/KnowledgeHub/Hubs/FileTable/FolderRow.vue'
import IndentGuide from '@/modules/Workspaces/KnowledgeHub/Hubs/FileTable/IndentGuide.vue'
import type { TreeNode } from '@/modules/Workspaces/KnowledgeHub/Hubs/FileTree/tree'
import type { Api } from '@zag-js/tree-view'
import { computed } from 'vue'

const props = defineProps<{
  node: TreeNode
  indexPath: number[]
  api: Api
}>()

const nodeProps = computed(() => ({
  indexPath: props.indexPath,
  node: props.node,
}))

const nodeState = computed(() => props.api.getNodeState(nodeProps.value))

const depth = computed(() => nodeState.value.depth)
const hasNextSibling = computed(() => !!props.api.collection.getNextSibling(props.indexPath))
</script>

<template>
  <template v-if="nodeState.isBranch">
    <div
      v-bind="api.getBranchProps(nodeProps)"
      class="relative"
    >
      <div
        v-bind="api.getBranchControlProps(nodeProps)"
        class="rounded-lg focus-visible:bg-background-transparent-hovered focus-visible:outline-none"
      >
        <IndentGuide
          :depth="depth"
          :has-next-sibling="hasNextSibling"
          class="absolute top-0 left-1.5"
        />
        <FolderRow
          v-if="node.type === 'folder'"
          :name="node.name"
          :path="node.path"
          :is-expanded="nodeState.expanded"
        />
      </div>

      <div v-bind="api.getBranchContentProps(nodeProps)">
        <TreeNode
          v-for="(childNode, index) in node.children"
          :key="childNode.id"
          :node="childNode"
          :index-path="[...indexPath, index]"
          :api="api"
        />
      </div>
    </div>
  </template>
  <template v-else>
    <div class="relative">
      <IndentGuide
        :depth="depth"
        :has-next-sibling="hasNextSibling"
        class="absolute top-0 left-1.5"
      />
      <HubTable.DataRow
        v-if="node.type === 'file'"
        class="rounded-lg focus-visible:bg-background-transparent-hovered focus-visible:outline-none"
        :item="node.file"
        v-bind="api.getItemProps(nodeProps)"
      />
    </div>
  </template>
</template>

<style lang="css" scoped>
/*
 * 20px is width of the indent guide
 * See SVG in `IndentGuide.vue`
 */
:deep([data-depth]) [data-tree-padded-cell] {
  padding-left: calc(20px * (var(--depth) - 1));
}
</style>
